import React, { useState } from 'react';
import { authenticatedFetch } from '../../../utils/authenticatedFetch';
import { API_ENDPOINTS } from '../../../config/apiRouting';

interface OnboardingData {
  restaurantName: string;
  cuisine: string;
  ownerEmail: string;
  posType: string;
  googleConnected: boolean;
  yelpUrl: string;
  brandVoice: {
    tone: string;
    style: string;
    signOff: string;
  };
}

interface ReviewPlatformSetupProps {
  data: OnboardingData;
  onUpdate: (data: Partial<OnboardingData>) => void;
  storeId: string;
}

export const ReviewPlatformSetup: React.FC<ReviewPlatformSetupProps> = ({
  data,
  onUpdate,
  storeId,
}) => {
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleConnect = async () => {
    setGoogleLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINTS.socialMediaAccounts()}/google/auth`,
        {
          method: 'POST',
          body: JSON.stringify({
            storeId,
            redirectUri: window.location.href,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.authUrl) {
          window.location.href = result.authUrl;
        } else {
          throw new Error('No authentication URL returned');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to initiate Google authentication');
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to connect to Google. Please try again.'
      );
      setGoogleLoading(false);
    }
  };

  const handleYelpUrlChange = (value: string) => {
    onUpdate({ yelpUrl: value });
  };

  const getConfiguredCount = (): number => {
    let count = 0;
    if (data.restaurantName) count++;
    if (data.posType && data.posType !== 'none') count++;
    if (data.googleConnected) count++;
    if (data.yelpUrl) count++;
    return count;
  };

  return (
    <div>
      <h2>Review Platforms</h2>
      <p className="wizard-step-description">
        Connect your review platforms to enable automated review monitoring, AI-powered response
        generation, and reputation management. You can always configure these later.
      </p>

      {error && (
        <div className="wizard-alert wizard-alert-error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
        </div>
      )}

      {/* Google Business Section */}
      <div className="review-platform-section">
        <h3>
          <i className="fab fa-google" style={{ color: '#4285f4' }}></i>
          Google Business Profile
        </h3>
        <p>
          Connect your Google Business Profile to monitor reviews, respond to customers, and
          track your online reputation on Google Search and Maps.
        </p>

        {data.googleConnected ? (
          <div className="review-platform-connected">
            <i className="fas fa-check-circle"></i>
            Google Business Profile connected
            {(data as any).googleSimulated && (
              <span className="simulated-badge">Simulated</span>
            )}
            <button
              className="disconnect-link"
              onClick={() => onUpdate({ googleConnected: false } as any)}
            >
              Disconnect
            </button>
          </div>
        ) : (
          <div className="google-connect-options">
            <button
              className="google-connect-btn"
              onClick={handleGoogleConnect}
              disabled={googleLoading}
            >
              {googleLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Connecting...
                </>
              ) : (
                <>
                  <i className="fab fa-google"></i>
                  Connect Google Business
                </>
              )}
            </button>
            <div className="simulate-option">
              <p className="simulate-divider">or</p>
              <button
                className="simulate-btn"
                onClick={() => onUpdate({ googleConnected: true } as any)}
              >
                <i className="fas fa-play-circle"></i>
                Simulate Google Connection
              </button>
              <small>Skip real OAuth and mark as connected for setup purposes</small>
            </div>
          </div>
        )}
      </div>

      {/* Yelp Section */}
      <div className="review-platform-section">
        <h3>
          <i className="fab fa-yelp" style={{ color: '#d32323' }}></i>
          Yelp Business Page
        </h3>
        <p>
          Enter your Yelp business page URL to enable review monitoring and analytics for your
          Yelp presence.
        </p>

        <div className="wizard-form-group">
          <label>Yelp Business URL</label>
          <input
            type="url"
            value={data.yelpUrl}
            onChange={(e) => handleYelpUrlChange(e.target.value)}
            placeholder="https://www.yelp.com/biz/your-restaurant-city"
          />
          <small>
            Find your URL by searching for your restaurant on Yelp and copying the page address
          </small>
        </div>
      </div>

      {/* Setup Summary */}
      <div className="setup-summary">
        <h3>Setup Summary ({getConfiguredCount()} items configured)</h3>
        <div className="summary-grid">
          {/* Restaurant Profile */}
          <div className="summary-item">
            <div
              className={`summary-item-icon ${data.restaurantName ? 'configured' : 'pending'}`}
            >
              <i className={`fas ${data.restaurantName ? 'fa-check' : 'fa-utensils'}`}></i>
            </div>
            <div className="summary-item-content">
              <div className="summary-item-label">Restaurant Profile</div>
              <div className="summary-item-value">
                {data.restaurantName
                  ? `${data.restaurantName}${data.cuisine ? ` - ${data.cuisine}` : ''}`
                  : 'Not configured'}
              </div>
            </div>
          </div>

          {/* Brand Voice */}
          <div className="summary-item">
            <div
              className={`summary-item-icon ${
                data.brandVoice.tone ? 'configured' : 'pending'
              }`}
            >
              <i className={`fas ${data.brandVoice.tone ? 'fa-check' : 'fa-comment'}`}></i>
            </div>
            <div className="summary-item-content">
              <div className="summary-item-label">Brand Voice</div>
              <div className="summary-item-value">
                {data.brandVoice.tone
                  ? `${data.brandVoice.tone}, signed by ${data.brandVoice.signOff || 'the management team'}`
                  : 'Using defaults'}
              </div>
            </div>
          </div>

          {/* POS Connection */}
          <div className="summary-item">
            <div
              className={`summary-item-icon ${
                data.posType && data.posType !== 'none'
                  ? 'configured'
                  : data.posType === 'none'
                    ? 'skipped'
                    : 'pending'
              }`}
            >
              <i
                className={`fas ${
                  data.posType && data.posType !== 'none'
                    ? 'fa-check'
                    : data.posType === 'none'
                      ? 'fa-minus'
                      : 'fa-cash-register'
                }`}
              ></i>
            </div>
            <div className="summary-item-content">
              <div className="summary-item-label">POS Connection</div>
              <div className="summary-item-value">
                {data.posType && data.posType !== 'none'
                  ? `${data.posType.charAt(0).toUpperCase() + data.posType.slice(1)} POS connected`
                  : data.posType === 'none'
                    ? 'Skipped - No POS selected'
                    : 'Not configured'}
              </div>
            </div>
          </div>

          {/* Google Business */}
          <div className="summary-item">
            <div
              className={`summary-item-icon ${
                data.googleConnected ? 'configured' : 'pending'
              }`}
            >
              <i className={`fas ${data.googleConnected ? 'fa-check' : 'fa-search'}`}></i>
            </div>
            <div className="summary-item-content">
              <div className="summary-item-label">Google Business</div>
              <div className="summary-item-value">
                {data.googleConnected ? 'Connected' : 'Not connected'}
              </div>
            </div>
          </div>

          {/* Yelp */}
          <div className="summary-item">
            <div
              className={`summary-item-icon ${data.yelpUrl ? 'configured' : 'pending'}`}
            >
              <i className={`fas ${data.yelpUrl ? 'fa-check' : 'fa-star'}`}></i>
            </div>
            <div className="summary-item-content">
              <div className="summary-item-label">Yelp Business Page</div>
              <div className="summary-item-value">
                {data.yelpUrl ? data.yelpUrl : 'Not configured'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewPlatformSetup;
